import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { CartProvider } from '@/context/CartContext';
import { Toaster } from '@/components/ui/sonner';

// Layout Components
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';

// Pages
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import WebinarsPage from '@/pages/WebinarsPage';
import WebinarDetailPage from '@/pages/WebinarDetailPage';
import SeminarsPage from '@/pages/SeminarsPage';
import SeminarDetailPage from '@/pages/SeminarDetailPage';
import SpeakersPage from '@/pages/SpeakersPage';
import SpeakerDetailPage from '@/pages/SpeakerDetailPage';
import IndustriesPage from '@/pages/IndustriesPage';
import IndustryDetailPage from '@/pages/IndustryDetailPage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import CartPage from '@/pages/CartPage';
import CheckoutPage from '@/pages/CheckoutPage';
import CheckoutSuccessPage from '@/pages/CheckoutSuccessPage';
import JoinSpeakerPage from '@/pages/JoinSpeakerPage';
import SpeakerDashboard from '@/pages/SpeakerDashboard';
import { TermsOfUsePage, PrivacyPolicyPage, RefundPolicyPage } from '@/pages/StaticPages';
import ArticlesPage from '@/pages/ArticlesPage';
import ArticleDetailPage from '@/pages/ArticleDetailPage';
import BundleLandingPage from '@/pages/BundleLandingPage';
import BundlesListPage from '@/pages/BundlesListPage';
import DashboardLayout, {
  DashboardOverview,
  MyWebinars,
  MyCertificates,
  MyOrders,
  MyProfile,
} from '@/pages/DashboardPage';

// Admin Pages
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminSpeakers from '@/pages/admin/AdminSpeakers';
import AdminWebinars from '@/pages/admin/AdminWebinars';
import AdminSeminarsPage from '@/pages/admin/AdminSeminarsPage';
import AdminSeriesPage from '@/pages/admin/AdminSeriesPage';
import AdminBundlesPage from '@/pages/admin/AdminBundlesPage';
import AdminIndustries from '@/pages/admin/AdminIndustries';
import AdminGallery from '@/pages/admin/AdminGallery';
import AdminOrders from '@/pages/admin/AdminOrders';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminLeadsPage from '@/pages/admin/AdminLeadsPage';
import AdminAnalyticsPage from '@/pages/admin/AdminAnalyticsPage';
import AdminEmailsPage from '@/pages/admin/AdminEmailsPage';
import AdminEmailTemplatesPage from '@/pages/admin/AdminEmailTemplatesPage';
import AdminEmailBuilderPage from '@/pages/admin/AdminEmailBuilderPage';
import AdminUnsubscribesPage from '@/pages/admin/AdminUnsubscribesPage';
import AdminEmailSchedulingPage from '@/pages/admin/AdminEmailSchedulingPage';
import AdminCorporateAccountsPage from '@/pages/admin/AdminCorporateAccountsPage';
import AdminPromoCodesPage from '@/pages/admin/AdminPromoCodesPage';
import AdminRecordingsPage from '@/pages/admin/AdminRecordingsPage';
import AdminContentManager from '@/pages/admin/AdminContentManager';
import AdminArticlesPage from '@/pages/admin/AdminArticlesPage';
import AdminGoLivePage from '@/pages/admin/AdminGoLivePage';
import AdminLandingPages from '@/pages/admin/AdminLandingPages';
import AdminLandingPageEditor from '@/pages/admin/AdminLandingPageEditor';
import AdminInvoices from '@/pages/admin/AdminInvoices';
import AdminReports from '@/pages/admin/AdminReports';
import LandingPageView from '@/pages/LandingPageView';
import InvoicePreview from '@/components/invoice/InvoicePreview';

// Components
import AnonymousUserPrompt from '@/components/AnonymousUserPrompt';
import ScrollToTop from '@/components/common/ScrollToTop';
import ScrollToTopButton from '@/components/common/ScrollToTopButton';
import CouponPopup from '@/components/common/CouponPopup';

// Hooks

import '@/App.css';
import { HelmetProvider } from 'react-helmet-async';

// Layout wrapper for public pages
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </>
);

// Layout wrapper for auth pages (no footer)
const AuthLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
  </>
);

// Layout wrapper for dashboard (with sidebar, no footer)
const DashboardWrapper = () => (
  <>
    <Navbar />
    <DashboardLayout />
  </>
);

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ScrollToTopButton />
          <CouponPopup />
            <div className="min-h-screen bg-white">
              
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/"
                  element={
                    <PublicLayout>
                      <HomePage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/webinars"
                  element={
                    <PublicLayout>
                      <WebinarsPage />
                    </PublicLayout>
                  }
                />
                <Route
                  path="/webinars/:id"
                  element={
                  <PublicLayout>
                    <WebinarDetailPage />
                  </PublicLayout>
              }
            />
            <Route
              path="/seminars"
              element={
                <PublicLayout>
                  <SeminarsPage />
                </PublicLayout>
              }
            />
            <Route
              path="/seminars/:id"
              element={
                <PublicLayout>
                  <SeminarDetailPage />
                </PublicLayout>
              }
            />
            <Route
              path="/speakers"
              element={<Navigate to="/" replace />}
            />
            <Route
              path="/speakers/:id"
              element={
                <PublicLayout>
                  <SpeakerDetailPage />
                </PublicLayout>
              }
            />
            <Route
              path="/industries"
              element={
                <PublicLayout>
                  <IndustriesPage />
                </PublicLayout>
              }
            />
            <Route
              path="/industries/:id"
              element={
                <PublicLayout>
                  <IndustryDetailPage />
                </PublicLayout>
              }
            />
            <Route
              path="/about"
              element={
                <PublicLayout>
                  <AboutPage />
                </PublicLayout>
              }
            />
            <Route
              path="/contact"
              element={
                <PublicLayout>
                  <ContactPage />
                </PublicLayout>
              }
            />
            
            {/* New Pages - Explore Section */}
            <Route
              path="/join-speaker"
              element={
                <PublicLayout>
                  <JoinSpeakerPage />
                </PublicLayout>
              }
            />
            <Route
              path="/terms-of-use"
              element={
                <PublicLayout>
                  <TermsOfUsePage />
                </PublicLayout>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <PublicLayout>
                  <PrivacyPolicyPage />
                </PublicLayout>
              }
            />
            <Route
              path="/refund-policy"
              element={
                <PublicLayout>
                  <RefundPolicyPage />
                </PublicLayout>
              }
            />
            <Route
              path="/articles"
              element={
                <PublicLayout>
                  <ArticlesPage />
                </PublicLayout>
              }
            />
            <Route
              path="/articles/:slug"
              element={
                <PublicLayout>
                  <ArticleDetailPage />
                </PublicLayout>
              }
            />
            <Route
              path="/bundles"
              element={
                <PublicLayout>
                  <BundlesListPage />
                </PublicLayout>
              }
            />
            <Route
              path="/bundles/:slug"
              element={
                <PublicLayout>
                  <BundleLandingPage />
                </PublicLayout>
              }
            />

            {/* Auth Routes */}
            <Route
              path="/login"
              element={
                <AuthLayout>
                  <LoginPage />
                </AuthLayout>
              }
            />
            <Route
              path="/register"
              element={
                <AuthLayout>
                  <RegisterPage />
                </AuthLayout>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <AuthLayout>
                  <ForgotPasswordPage />
                </AuthLayout>
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <AuthLayout>
                  <ResetPasswordPage />
                </AuthLayout>
              }
            />
            
            {/* Cart & Checkout Routes */}
            <Route
              path="/cart"
              element={
                <PublicLayout>
                  <CartPage />
                </PublicLayout>
              }
            />
            <Route
              path="/checkout"
              element={
                <PublicLayout>
                  <CheckoutPage />
                </PublicLayout>
              }
            />
            <Route
              path="/checkout/success"
              element={
                <PublicLayout>
                  <CheckoutSuccessPage />
                </PublicLayout>
              }
            />

            {/* Dashboard Routes */}
            <Route path="/dashboard" element={<DashboardWrapper />}>
              <Route index element={<DashboardOverview />} />
              <Route path="webinars" element={<MyWebinars />} />
              <Route path="certificates" element={<MyCertificates />} />
              <Route path="orders" element={<MyOrders />} />
              <Route path="profile" element={<MyProfile />} />
            </Route>

            {/* Speaker Dashboard */}
            <Route
              path="/speaker-dashboard"
              element={
                <PublicLayout>
                  <SpeakerDashboard />
                </PublicLayout>
              }
            />

            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="speakers" element={<AdminSpeakers />} />
              <Route path="webinars" element={<AdminWebinars />} />
              <Route path="seminars" element={<AdminSeminarsPage />} />
              <Route path="series" element={<AdminSeriesPage />} />
              <Route path="bundles" element={<AdminBundlesPage />} />
              <Route path="industries" element={<AdminIndustries />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="leads" element={<AdminLeadsPage />} />
              <Route path="analytics" element={<AdminAnalyticsPage />} />
              <Route path="emails" element={<AdminEmailsPage />} />
              <Route path="email-templates" element={<AdminEmailTemplatesPage />} />
              <Route path="email-builder" element={<AdminEmailBuilderPage />} />
              <Route path="email-builder/:id" element={<AdminEmailBuilderPage />} />
              <Route path="unsubscribes" element={<AdminUnsubscribesPage />} />
              <Route path="email-scheduling" element={<AdminEmailSchedulingPage />} />
              <Route path="corporate-accounts" element={<AdminCorporateAccountsPage />} />
              <Route path="promo-codes" element={<AdminPromoCodesPage />} />
              <Route path="recordings" element={<AdminRecordingsPage />} />
              <Route path="content-manager" element={<AdminContentManager />} />
              <Route path="articles" element={<AdminArticlesPage />} />
              <Route path="go-live" element={<AdminGoLivePage />} />
              <Route path="landing-pages" element={<AdminLandingPages />} />
              <Route path="landing-pages/:id" element={<AdminLandingPageEditor />} />
            </Route>

            {/* Public Landing Pages */}
            <Route path="/lp/:slug" element={<LandingPageView />} />

            {/* Invoice Preview (Admin Only) */}
            <Route path="/admin/invoice-preview" element={<InvoicePreview />} />
            <Route path="/admin/invoice/:orderId" element={<InvoicePreview />} />

            {/* Catch-all route */}
            <Route
              path="*"
              element={
                <PublicLayout>
                  <div className="min-h-screen flex items-center justify-center pt-20">
                    <div className="text-center">
                      <h1 className="font-heading text-4xl font-bold text-navy-700 mb-4">
                        404
                      </h1>
                      <p className="text-slate-600 mb-6">Page not found</p>
                      <a
                        href="/"
                        className="inline-flex items-center justify-center px-6 py-3 bg-navy-500 text-white rounded-md hover:bg-navy-600"
                      >
                        Go Home
                      </a>
                    </div>
                  </div>
                </PublicLayout>
              }
            />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
      </CartProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
